import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import * as API from "../../api";

/**
 * FAQ — redesigned to match the Tailwind design system used on Home/Contact
 * (eyebrow badge + 2-line display title, brand/accent colors, rounded cards,
 * shadow-card). Data logic is unchanged from the legacy MUI page: same
 * API.FAQ() call, same response shape (res.data is an array of
 * { question, answer }). Only the JSX/styling was rewritten and a native
 * accordion replaces the MUI Collapse.
 */

function FaqItem({ question, answer, isOpen, onToggle, index }) {
  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-button-${index}`;
  return (
    <div
      className={`overflow-hidden rounded-2xl border bg-white transition-all duration-200 ${
        isOpen
          ? "border-brand-200 shadow-card-hover"
          : "border-slate-100 shadow-card hover:border-slate-200"
      }`}
    >
      <button
        type="button"
        id={buttonId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        className="flex w-full items-center gap-4 px-5 py-4 text-start sm:px-6 sm:py-5"
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors ${
            isOpen ? "bg-brand-500 text-white" : "bg-brand-50 text-brand-500"
          }`}
        >
          <QuestionMarkCircleIcon className="h-5 w-5" />
        </span>
        <h3 className="flex-1 font-display text-base font-semibold text-ink sm:text-lg">
          {question}
        </h3>
        <ChevronDownIcon
          className={`h-5 w-5 shrink-0 text-ink-subtle transition-transform duration-300 ${
            isOpen ? "rotate-180 text-brand-500" : ""
          }`}
        />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={`grid transition-all duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-5 pb-5 ps-[4.75rem] text-sm leading-relaxed text-ink-muted sm:px-6 sm:pb-6">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

const FQA = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    API.FAQ()
      .then((res) => {
        if (alive) setData(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
      })
      .then(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="bg-surface">
      {/* Hero — mirrors Home/Contact hero (eyebrow badge + 2-line title) */}
      <section className="relative overflow-hidden bg-surface-muted">
        <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 lg:pt-16 pb-10 sm:pb-14">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700 shadow-card border border-slate-200/80">
              {t("faqs.eyebrow", "Centre d'aide")}
            </span>
            <h1 className="mt-4 font-display font-extrabold text-ink leading-[1.15] tracking-tight text-[1.75rem] sm:text-4xl lg:text-5xl">
              <span className="block text-balance">
                {t("faqs.heroTitle", "Questions fréquentes")}
              </span>
              <span className="inline-block text-brand-500 mt-1.5">
                {t("faqs.heroTitleAccent", "On vous répond.")}
              </span>
            </h1>
            <p className="mt-4 text-sm sm:text-base text-ink-muted leading-relaxed">
              {t(
                "faqs.heroSubtitle",
                "Réservez en deux clics des expériences inoubliables. Retrouvez ici les réponses aux questions les plus posées."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Accordion */}
      <section className="relative py-10 sm:py-14 lg:py-16">
        <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="space-y-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-2xl border border-slate-100 bg-white shadow-card"
                />
              ))}
            </div>
          ) : data.length === 0 ? (
            <div className="rounded-2xl border border-slate-100 bg-white p-10 text-center shadow-card">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
                <QuestionMarkCircleIcon className="h-6 w-6" />
              </span>
              <p className="mt-4 text-sm font-medium text-ink-muted">
                {t("faqs.empty", "Aucune question pour le moment.")}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {data.map((item, index) => (
                <FaqItem
                  key={index}
                  index={index}
                  question={item.question}
                  answer={item.answer}
                  isOpen={openIndex === index}
                  onToggle={() =>
                    setOpenIndex((cur) => (cur === index ? -1 : index))
                  }
                />
              ))}
            </div>
          )}

          {/* Still need help — CTA to contact */}
          <div className="mt-12 rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-card sm:p-8">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-500/15 text-accent-700">
              <ChatBubbleLeftRightIcon className="h-6 w-6" />
            </span>
            <h2 className="mt-4 font-display text-lg font-semibold text-ink sm:text-xl">
              {t("faqs.ctaTitle", "Vous ne trouvez pas votre réponse ?")}
            </h2>
            <p className="mt-2 text-sm text-ink-muted">
              {t(
                "faqs.ctaSubtitle",
                "Notre équipe est là pour vous aider. Contactez-nous, on vous répond sous 24h."
              )}
            </p>
            <Link
              to="/contact"
              className="mt-5 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-brand-500 px-6 font-semibold text-white shadow-card transition-colors hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" />
              <span>{t("faqs.ctaButton", "Contactez-nous")}</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FQA;
